/**
 * Analytics - Event tracking and user analytics
 */

// Analytics configuration
const config = {
  enabled: import.meta.env.PROD,
  debug: import.meta.env.DEV,
}

// Event types
export const Events = {
  // Page views
  PAGE_VIEW: 'page_view',
  
  // User actions
  SIGN_IN: 'user_sign_in',
  SIGN_UP: 'user_sign_up',
  SIGN_OUT: 'user_sign_out',
  
  // Document actions
  DOCUMENT_UPLOAD: 'document_upload',
  DOCUMENT_VIEW: 'document_view',
  DOCUMENT_VERIFY: 'document_verify',
  DOCUMENT_REJECT: 'document_reject',
  
  // Transaction actions
  TRANSACTION_CREATE: 'transaction_create',
  TRANSACTION_EDIT: 'transaction_edit',
  TRANSACTION_DELETE: 'transaction_delete',
  
  // Token actions
  TOKEN_GENERATE: 'token_generate',
  TOKEN_REVOKE: 'token_revoke',
  TOKEN_ACCESS: 'token_access',
  
  // Agent actions
  AGENT_REQUEST: 'agent_request',
  AGENT_ASSIGN: 'agent_assign',
  AGENT_COMPLETE: 'agent_complete',
  
  // Subscription actions
  SUBSCRIPTION_UPGRADE: 'subscription_upgrade',
  SUBSCRIPTION_DOWNGRADE: 'subscription_downgrade',
  
  // Error tracking
  ERROR: 'error',
  API_ERROR: 'api_error',
}

// Track event
export function trackEvent(eventName, properties = {}) {
  if (!config.enabled) {
    if (config.debug) {
      console.debug('[Analytics]', eventName, properties)
    }
    return
  }

  try {
    // Google Analytics (gtag)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, properties)
    }

    // Mixpanel
    if (typeof window !== 'undefined' && window.mixpanel) {
      window.mixpanel.track(eventName, properties)
    }

    // Custom analytics endpoint
    const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT
    if (endpoint && typeof window !== 'undefined') {
      const payload = {
        event: eventName,
        properties,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent || 'unknown',
        url: window.location.href || 'unknown',
      }
      
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(endpoint, JSON.stringify(payload))
        } else {
          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {})
        }
      } catch (beaconErr) {
        // Silently fail
      }
    }
  } catch (err) {
    // Silently fail to avoid breaking app
    if (config.debug) {
      console.debug('[Analytics] Error:', err)
    }
  }
}

// Track page view
export function trackPageView(path, title) {
  trackEvent(Events.PAGE_VIEW, {
    path: path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    title: title || (typeof document !== 'undefined' ? document.title : ''),
    referrer: typeof document !== 'undefined' ? document.referrer : '',
  })
}

// Identify user
export function identifyUser(userId, traits = {}) {
  if (!config.enabled || typeof window === 'undefined') return

  try {
    // Google Analytics
    if (window.gtag) {
      window.gtag('set', 'user_id', userId)
    }

    // Mixpanel
    if (window.mixpanel) {
      window.mixpanel.identify(userId)
      window.mixpanel.people.set(traits)
    }
  } catch (err) {
    if (config.debug) {
      console.debug('[Analytics] Identify error:', err)
    }
  }
}

// Track user action with timing
export function trackTimedEvent(eventName, callback) {
  const start = performance ? performance.now() : 0
  const result = callback()
  const duration = performance ? Math.round(performance.now() - start) : 0
  
  trackEvent(eventName, { duration })
  return result
}

// Track error
export function trackError(error, context = {}) {
  trackEvent(Events.ERROR, {
    message: error?.message || 'Unknown error',
    stack: error?.stack,
    ...context,
  })
}

export default {
  Events,
  trackEvent,
  trackPageView,
  identifyUser,
  trackTimedEvent,
  trackError,
}